import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import {
  fetchProductOrders,
  fetchProductOrderByUuid,
} from "@/modules/productStorefront/features/ProductOrderManagement/api/productOrderApi";

export interface ProductOrderListItem {
  id: number;
  uuid: string;
  confirmationNumber: string | null;
  status: string;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  subtotal: number;
  discountAmount: number;
  total: number;
  itemCount: number;
  createdAt: string | null;
  completedAt: string | null;
}

export interface ProductOrderDetail {
  id: number;
  uuid: string;
  confirmationNumber: string | null;
  status: string;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone: string | null;
  shippingAddressLine1: string | null;
  shippingAddressLine2: string | null;
  shippingCity: string | null;
  shippingState: string | null;
  shippingZip: string | null;
  subtotal: number;
  discountAmount: number;
  total: number;
  discountCode: string | null;
  notes: string | null;
  completedAt: string | null;
  createdAt: string | null;
  items: Array<{
    id: number;
    productId: number;
    productName: string;
    productCode: string;
    productType: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    isShipped: boolean;
    shippedAt: string | null;
    trackingNumber: string | null;
    notes: string | null;
  }>;
}

interface ProductOrderListState {
  orders: ProductOrderListItem[];
  totalCount: number;
  fetchLoading: boolean;
  fetchError: string | null;
  selectedOrder: ProductOrderDetail | null;
  fetchDetailLoading: boolean;
  fetchDetailError: string | null;
}

const initialState: ProductOrderListState = {
  orders: [],
  totalCount: 0,
  fetchLoading: false,
  fetchError: null,
  selectedOrder: null,
  fetchDetailLoading: false,
  fetchDetailError: null,
};

export const productOrderListSlice = createSlice({
  name: "productOrderList",
  initialState,
  reducers: {
    setFetchLoading(state, action: PayloadAction<boolean>) {
      state.fetchLoading = action.payload;
    },
    setFetchError(state, action: PayloadAction<string | null>) {
      state.fetchError = action.payload;
    },
    setOrders(state, action: PayloadAction<ProductOrderListItem[]>) {
      state.orders = action.payload;
    },
    setTotalCount(state, action: PayloadAction<number>) {
      state.totalCount = action.payload;
    },
    setFetchDetailLoading(state, action: PayloadAction<boolean>) {
      state.fetchDetailLoading = action.payload;
    },
    setFetchDetailError(state, action: PayloadAction<string | null>) {
      state.fetchDetailError = action.payload;
    },
    setSelectedOrder(state, action: PayloadAction<ProductOrderDetail | null>) {
      state.selectedOrder = action.payload;
    },
    resetProductOrderListState(state) {
      state.orders = [];
      state.totalCount = 0;
      state.fetchLoading = false;
      state.fetchError = null;
      state.selectedOrder = null;
      state.fetchDetailLoading = false;
      state.fetchDetailError = null;
    },
  },
});

export default productOrderListSlice.reducer;

export const {
  setFetchLoading,
  setFetchError,
  setOrders,
  setTotalCount,
  setFetchDetailLoading,
  setFetchDetailError,
  setSelectedOrder,
  resetProductOrderListState,
} = productOrderListSlice.actions;

export const fetchProductOrderListAction =
  (params: Record<string, any>): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setFetchLoading(true));
    dispatch(setFetchError(null));
    try {
      const result = await fetchProductOrders(params);
      dispatch(setOrders(result.data));
      dispatch(setTotalCount(result.meta?.totalCount ?? 0));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch product orders.";
      dispatch(setFetchError(message));
    } finally {
      dispatch(setFetchLoading(false));
    }
  };

export const fetchProductOrderDetailAction =
  (uuid: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setFetchDetailLoading(true));
    dispatch(setFetchDetailError(null));
    try {
      const result = await fetchProductOrderByUuid(uuid);
      dispatch(setSelectedOrder(result.data));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch product order details.";
      dispatch(setFetchDetailError(message));
    } finally {
      dispatch(setFetchDetailLoading(false));
    }
  };
